import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Component, useEffect, useMemo, useRef, useState } from "react";
import { Color, Vector2, Vector3, Quaternion, Raycaster, Plane } from "three";
import { SimplexNoise } from "three/addons/math/SimplexNoise.js";
import { PlaneGeometry, BufferAttribute } from "three";




function Points() {
  const [mousePos, setMousePos] = useState({});

  useEffect(() => {
    const handleMouseMove = (event) => {
      setMousePos({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener(
        'mousemove',
        handleMouseMove
      );
    };
  }, []);

  const pointsRef = useRef(null);
  const simplex = useMemo(() => new SimplexNoise(), []);
  const { camera, pointer } = useThree();

  const raycaster = useMemo(() => new Raycaster(), []);
  // Temporary / reusable vectors to avoid allocations inside loops
  const tmpWorldPos = useMemo(() => new Vector3(), []);
  const tmpNormal = useMemo(() => new Vector3(), []);
  const tmpIntersectWorld = useMemo(() => new Vector3(), []);
  const tmpIntersectLocal = useMemo(() => new Vector3(), []);

  // Create geometry & vertex colors (local-space coords)
  const { geometry } = useMemo(() => {
    const segments = 150;
    const planeGeom = new PlaneGeometry(25, 25, segments, segments);
    const colorArray = new Float32Array(planeGeom.attributes.position.count * 3);
    for (let i = 0; i < planeGeom.attributes.position.count; i++) {
      colorArray[i * 3 + 0] = 1.0;
      colorArray[i * 3 + 1] = 1.0;
      colorArray[i * 3 + 2] = 1.0;
    }
    planeGeom.setAttribute("color", new BufferAttribute(colorArray, 3));
    return { geometry: planeGeom };
  }, []);

  useFrame(({ clock }) => {


    const time = clock.getElapsedTime();
    const pos = geometry.attributes.position;
    const color = geometry.attributes.color;

    // Update Z displacement first (local coords)
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = simplex.noise3d(x * 0.3, y * 0.3, time * 0.5) * 0.5;
      pos.setZ(i, z);
    }

    // Build a plane in world-space that matches the points mesh orientation/position
    if (pointsRef.current) {
      // geometry's +Z normal (0,0,1) transformed by mesh quaternion => world normal
      tmpNormal.set(0, 0, 1).applyQuaternion(pointsRef.current.quaternion);
      // a coplanar point in world coords
      pointsRef.current.getWorldPosition(tmpWorldPos);
      const worldPlane = new Plane().setFromNormalAndCoplanarPoint(tmpNormal, tmpWorldPos);


      // console.log(pointer)
      // pointer = [mousePos.x, mousePos.y]
      // console.log(mousePos.x)

      var normPos = [(mousePos.x / window.innerWidth * 2 - 1), (mousePos.y / window.innerHeight) * 2 + 1];
      const po = (() => new Vector2(), [((mousePos.x / window.innerWidth) * 2 - 1), -((mousePos.y / window.innerHeight) * 2 + 1)-2]);

      // console.log(po)
      // console.log(pointer)

      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.ray.intersectPlane(worldPlane, tmpIntersectWorld);

      if (hit) {
        // Convert the intersection point into the mesh's local space once
        pointsRef.current.worldToLocal(tmpIntersectLocal.copy(tmpIntersectWorld));
      } else {
        // No hit -> put the local intersect far away so no points highlight
        tmpIntersectLocal.set(1e9, 1e9, 1e9);
      }
    } else {
      tmpIntersectLocal.set(1e9, 1e9, 1e9);
    }

    // Now compute colors (local-to-local comparison)
    const radius = 0.6; // tweak as needed
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);

      // base twinkle
      const twinkle = 0.5 + 0.5 * Math.sin(time * 5 + i);
      let r = twinkle;
      let g = Math.floor(Math.random() * 1.1) * twinkle;
      let b = Math.floor(Math.random() * 1.1) * twinkle;

      // distance in local space
      const dx = x - tmpIntersectLocal.x;
      const dy = y - tmpIntersectLocal.y;
      const dz = z - tmpIntersectLocal.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < radius) {
        const t = 1 - dist / radius; // 1 at center, 0 at edge
        const white = 1.0 * t + r * (1 - t); // blend white with base color
        r = g = b = white;
      }

      color.setXYZ(i, r, g, b);
    }



    pos.needsUpdate = true;
    color.needsUpdate = true;
  });

  return (
    <points ref={pointsRef} rotation-x={-Math.PI / 2}>
      <primitive object={geometry} attach="geometry" />
      <pointsMaterial
        size={0.03}
        sizeAttenuation
        vertexColors
        transparent
      />
    </points>
  );
}



export default function Waves() {
  return (
    <Canvas camera={{ position: [4, 2, 2], }}>
      <ambientLight intensity={0.5} />
      <Points />
    </Canvas>
  )
}
